import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "./ui/Toast";
import { Badge, Button, Card, Input, Modal, Select, Textarea } from "./ui";
import { ConfirmModal } from "./ui/Modal";

const DOCS_URL = "/api/v1/fornitori-api/docs";

const formatData = (iso) =>
  iso
    ? new Date(iso).toLocaleString("it-IT", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "mai";

/**
 * Tab "Integrazione API" nel dettaglio fornitore (solo Admin).
 * Gestisce le credenziali HMAC con cui il fornitore invia interventi e rapportini
 * dal proprio sistema. Il segreto si consegna con "Invia via email" o lo legge il
 * fornitore stesso dal proprio Profilo nel portale.
 */
function FornitoreApiTab({ fornitoreId, fornitoreNome }) {
  const { apiRequest } = useAuth();
  const { showToast } = useToast();
  const base = `/fornitori/${fornitoreId}/credenziali-api`;

  const [credenziali, setCredenziali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nuova, setNuova] = useState(null); // { nome, ipConsentiti } nel modal di creazione
  const [modifica, setModifica] = useState(null); // { id, nome, ipConsentiti }
  const [segretoMostrato, setSegretoMostrato] = useState(null); // { id, segreto }
  const [conferma, setConferma] = useState(null); // { tipo: 'ruota'|'elimina', credenziale }
  const [invio, setInvio] = useState(null); // { credenziale, destinatari, destinatario }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(base);
      setCredenziali(data.value || []);
    } catch (error) {
      showToast(error.message || "Errore caricamento credenziali", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, base, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const crea = async () => {
    setSaving(true);
    try {
      const res = await apiRequest(base, {
        method: "POST",
        body: JSON.stringify(nuova),
      });
      setNuova(null);
      setSegretoMostrato(res);
      load();
    } catch (error) {
      showToast(error.message || "Errore creazione credenziale", "error");
    } finally {
      setSaving(false);
    }
  };

  const salvaModifica = async () => {
    setSaving(true);
    try {
      await apiRequest(`${base}/${modifica.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          nome: modifica.nome,
          ipConsentiti: modifica.ipConsentiti,
        }),
      });
      setModifica(null);
      showToast("Credenziale aggiornata", "success");
      load();
    } catch (error) {
      showToast(error.message || "Errore aggiornamento", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleAttiva = async (c) => {
    try {
      await apiRequest(`${base}/${c.id}`, {
        method: "PATCH",
        body: JSON.stringify({ attiva: !c.attiva }),
      });
      load();
    } catch (error) {
      showToast(error.message || "Errore aggiornamento", "error");
    }
  };

  const eseguiConferma = async () => {
    const { tipo, credenziale } = conferma;
    setSaving(true);
    try {
      if (tipo === "ruota") {
        const res = await apiRequest(`${base}/${credenziale.id}/ruota`, {
          method: "POST",
        });
        setSegretoMostrato(res);
      } else {
        await apiRequest(`${base}/${credenziale.id}`, { method: "DELETE" });
        showToast("Credenziale eliminata", "success");
      }
      setConferma(null);
      load();
    } catch (error) {
      showToast(error.message || "Operazione non riuscita", "error");
    } finally {
      setSaving(false);
    }
  };

  const apriInvio = async (credenziale) => {
    try {
      const data = await apiRequest(`${base}/destinatari`);
      const destinatari = data.value || [];
      if (destinatari.length === 0) {
        showToast(
          "Nessun utente o referente con email registrato per questo fornitore",
          "warning",
        );
        return;
      }
      setInvio({
        credenziale,
        destinatari,
        destinatario: destinatari[0].email,
      });
    } catch (error) {
      showToast(error.message || "Errore caricamento destinatari", "error");
    }
  };

  const inviaEmail = async () => {
    setSaving(true);
    try {
      const res = await apiRequest(
        `${base}/${invio.credenziale.id}/invia-email`,
        {
          method: "POST",
          body: JSON.stringify({ destinatario: invio.destinatario }),
        },
      );
      setInvio(null);
      showToast(res.message || "Credenziali inviate", "success");
    } catch (error) {
      showToast(error.message || "Invio non riuscito", "error");
    } finally {
      setSaving(false);
    }
  };

  const copia = async (testo) => {
    try {
      await navigator.clipboard.writeText(testo);
      showToast("Copiato negli appunti", "success");
    } catch {
      showToast("Copia non riuscita: seleziona e copia a mano", "warning");
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold">Integrazione API</h3>
          <p className="text-sm text-gray-500 max-w-2xl">
            Credenziali con cui il fornitore invia interventi e rapportini dal
            proprio gestionale. Gli interventi arrivano da validare. Se indichi
            degli IP consentiti, le richieste da altri indirizzi vengono
            rifiutate.{" "}
            <a
              href={DOCS_URL}
              target="_blank"
              rel="noreferrer"
              className="text-primary-600 underline"
            >
              Documentazione API
            </a>
          </p>
        </div>
        <Button
          onClick={() =>
            setNuova({
              nome: `${fornitoreNome || "Fornitore"} - API`,
              ipConsentiti: "",
            })
          }
        >
          Nuova credenziale
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Caricamento...</p>
      ) : credenziali.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nessuna credenziale: il fornitore non puo' usare l'API.
        </p>
      ) : (
        <div className="space-y-3">
          {credenziali.map((c) => (
            <div
              key={c.id}
              className="border border-gray-200 rounded-lg p-4 flex flex-wrap gap-4 justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.nome}</span>
                  <Badge variant={c.attiva ? "success" : "default"}>
                    {c.attiva ? "Attiva" : "Disattivata"}
                  </Badge>
                </div>
                <p className="text-xs text-gray-500 break-all">
                  Key Id: <code>{c.id}</code>{" "}
                  <button
                    type="button"
                    className="text-primary-600 underline"
                    onClick={() => copia(c.id)}
                  >
                    copia
                  </button>
                </p>
                <p className="text-xs text-gray-500">
                  IP consentiti:{" "}
                  {c.ipConsentiti ? (
                    <code>{c.ipConsentiti.split(/\s+/).join(", ")}</code>
                  ) : (
                    "nessuna restrizione"
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  Ultimo utilizzo: {formatData(c.ultimoUtilizzo)}
                  {c.ultimoIp ? ` da ${c.ultimoIp}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-start gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setModifica({
                      id: c.id,
                      nome: c.nome,
                      ipConsentiti: c.ipConsentiti,
                    })
                  }
                >
                  Modifica
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAttiva(c)}
                >
                  {c.attiva ? "Disattiva" : "Attiva"}
                </Button>
                {c.attiva && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => apriInvio(c)}
                  >
                    Invia via email
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConferma({ tipo: "ruota", credenziale: c })}
                >
                  Nuovo segreto
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() =>
                    setConferma({ tipo: "elimina", credenziale: c })
                  }
                >
                  Elimina
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Creazione / modifica */}
      <Modal
        isOpen={!!nuova || !!modifica}
        onClose={() => {
          setNuova(null);
          setModifica(null);
        }}
        title={nuova ? "Nuova credenziale API" : "Modifica credenziale API"}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setNuova(null);
                setModifica(null);
              }}
            >
              Annulla
            </Button>
            <Button onClick={nuova ? crea : salvaModifica} disabled={saving}>
              {saving ? "Salvataggio..." : nuova ? "Crea" : "Salva"}
            </Button>
          </div>
        }
      >
        {(nuova || modifica) && (
          <div className="space-y-4">
            <Input
              label="Nome"
              value={(nuova || modifica).nome}
              onChange={(e) =>
                nuova
                  ? setNuova({ ...nuova, nome: e.target.value })
                  : setModifica({ ...modifica, nome: e.target.value })
              }
            />
            <Textarea
              label="IP consentiti (facoltativo)"
              rows={3}
              placeholder={"79.8.164.121\n10.0.0.0/24"}
              value={(nuova || modifica).ipConsentiti}
              onChange={(e) =>
                nuova
                  ? setNuova({ ...nuova, ipConsentiti: e.target.value })
                  : setModifica({ ...modifica, ipConsentiti: e.target.value })
              }
            />
            <p className="text-xs text-gray-500">
              Uno per riga: indirizzi IPv4/IPv6 o reti CIDR. Vuoto = nessuna
              restrizione.
            </p>
          </div>
        )}
      </Modal>

      {/* Segreto appena creato o rigenerato */}
      <Modal
        isOpen={!!segretoMostrato}
        onClose={() => setSegretoMostrato(null)}
        title="Credenziale pronta"
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setSegretoMostrato(null)}>Chiudi</Button>
          </div>
        }
      >
        {segretoMostrato && (
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Non serve copiarlo ora: puoi inviarlo al fornitore con "Invia via
              email", oppure gli utenti del fornitore lo leggono dal proprio
              Profilo nel portale.
            </div>
            {[
              ["Key Id", segretoMostrato.id],
              ["Segreto", segretoMostrato.segreto],
            ].map(([etichetta, valore]) => (
              <div key={etichetta}>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {etichetta}
                </p>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 bg-gray-100 rounded text-xs break-all">
                    {valore}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copia(valore)}
                  >
                    Copia
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Invio del segreto via email */}
      <Modal
        isOpen={!!invio}
        onClose={() => setInvio(null)}
        title="Invia credenziali via email"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInvio(null)}>
              Annulla
            </Button>
            <Button onClick={inviaEmail} disabled={saving}>
              {saving ? "Invio..." : "Invia"}
            </Button>
          </div>
        }
      >
        {invio && (
          <div className="space-y-4">
            <Select
              label="Destinatario"
              value={invio.destinatario}
              onChange={(e) =>
                setInvio({ ...invio, destinatario: e.target.value })
              }
              options={invio.destinatari.map((d) => ({
                value: d.email,
                label: `${d.nome} <${d.email}> (${d.tipo})`,
              }))}
            />
            <p className="text-sm text-gray-600">
              L'email contiene Key Id, segreto attuale, IP consentiti e il link
              alla documentazione. Il segreto non cambia: un'integrazione gia'
              attiva continua a funzionare. Sono ammessi solo utenti del portale
              e referenti registrati per questo fornitore.
            </p>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!conferma}
        onClose={() => setConferma(null)}
        onConfirm={eseguiConferma}
        loading={saving}
        title={
          conferma?.tipo === "ruota"
            ? "Generare un nuovo segreto?"
            : "Eliminare la credenziale?"
        }
        message={
          conferma?.tipo === "ruota"
            ? "Il segreto attuale smette di funzionare subito: il fornitore dovra' aggiornare il proprio sistema. Per cambiarlo senza interruzioni, crea una seconda credenziale e disattiva questa quando il fornitore ha migrato."
            : "Il fornitore non potra' piu' usare questa credenziale. L'operazione non si puo' annullare."
        }
        confirmText={conferma?.tipo === "ruota" ? "Genera" : "Elimina"}
        confirmVariant="danger"
      />
    </Card>
  );
}

export default FornitoreApiTab;
